import React, { useState } from "react";
import { CheckboxGroupItem } from "@/components/ui/checkbox-group";
import { Delete, Edit } from "@dreamhub/designsystem/icons";
import { ViewDataConfiguration } from "@/entities/views/types";
import { Draggable } from "@hello-pangea/dnd";
import { createPortal } from "react-dom";
import { cn } from "@/utils/utils";
import { IconButton } from "@/components/ui/icon-button";
import { GripVertical } from "lucide-react";
import NewEditableViewName from "@/components/layouts/ViewsSelection/NewEditableViewName";

interface DraggableViewItemProps {
  view: ViewDataConfiguration;
  index: number;
  onViewChange: (view: ViewDataConfiguration, checked: boolean) => void;
  onDeleteView: (view: ViewDataConfiguration) => void;
  onRename: (view: ViewDataConfiguration, newName: string) => void;
  i18n: any;
  isDragDisabled?: boolean;
  isCheckboxDisabled?: boolean;
  checkboxClassName?: string;
}

const NewDraggableViewItem: React.FC<DraggableViewItemProps> = ({
  view,
  index,
  onViewChange,
  onDeleteView,
  onRename,
  i18n,
  isDragDisabled = false,
  isCheckboxDisabled = false,
  checkboxClassName
}) => {
  const [isEditing, setIsEditing] = useState(false);

  const handleEditClick = () => {
    if (!view.isSystem) {
      setIsEditing(true);
    }
  };

  const handleEditComplete = () => {
    setIsEditing(false);
  };

  const content = (provided: any, snapshot: any, isClone: boolean = false) => (
    <div
      ref={isClone ? null : provided.innerRef}
      {...provided.draggableProps}
      className={cn(
        "flex items-center group relative p-2 pl-1 h-9 rounded-md !cursor-pointer",
        snapshot.isDragging && "bg-accent",
        !snapshot.isDragging && "hover:bg-surface-hover-soft"
      )}
      style={provided.draggableProps.style}
    >
      {!isDragDisabled && (
        <div
          {...provided.dragHandleProps}
          className={cn(
            "cursor-grab active:cursor-grabbing",
            "opacity-0 group-hover:opacity-100 transition-opacity"
          )}
        >
          <GripVertical className="h-4 w-4 text-secondary" />
        </div>
      )}
      <div className="flex items-center min-w-0 flex-1 gap-2">
        <CheckboxGroupItem
          id={view.id}
          label=""
          checked={view.isOpen}
          onCheckedChange={(checked: boolean) => onViewChange(view, checked)}
          disabled={isCheckboxDisabled}
          className="pointer-events-auto border-border-secondary-button"
          checkboxClassName={checkboxClassName}
        />
        <div className="flex-1 min-w-0">
          <NewEditableViewName
            view={view}
            i18n={i18n}
            onRename={onRename}
            isEditing={isEditing}
            onEditComplete={handleEditComplete}
          />
        </div>
      </div>
      {!view.isSystem && !isEditing && (
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
          <IconButton
            onClick={handleEditClick}
            className="pointer-events-auto"
            variant="iconNoBg"
            size="sm"
            iconColor="secondary"
          >
            <Edit />
          </IconButton>
          <IconButton
            data-test="delete-view"
            onClick={() => onDeleteView(view)}
            className="pointer-events-auto"
            destructive
            variant="iconNoBg"
            size="sm"
          >
            <Delete />
          </IconButton>
        </div>
      )}
    </div>
  );

  return (
    <Draggable
      draggableId={view.id}
      index={index}
      isDragDisabled={isDragDisabled}
    >
      {(provided, snapshot) => (
        <>
          {content(provided, snapshot)}
          {snapshot.isDragging &&
            createPortal(content(provided, snapshot, true), document.body)}
        </>
      )}
    </Draggable>
  );
};

export default NewDraggableViewItem;
